import React, { Component, ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "./button";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: undefined });
    window.location.reload();
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          className="min-h-[400px] flex items-center justify-center p-8"
          data-oid="2dfiaxa"
        >
          <div className="text-center max-w-md" data-oid="b53s-ew">
            <div className="mb-6" data-oid="cssq4gs">
              <AlertTriangle
                className="w-16 h-16 text-red-500 mx-auto mb-4"
                data-oid="4vb:_x8"
              />
              <h2
                className="text-2xl font-bold text-foreground mb-2"
                data-oid="xj47ynb"
              >
                Something went wrong
              </h2>
              <p className="text-muted-foreground" data-oid="crkii:a">
                We encountered an error while loading this component. This might
                be a temporary issue.
              </p>
            </div>

            <div className="space-y-3" data-oid="9kyrlmw">
              <Button
                onClick={this.handleRetry}
                variant="default"
                className="w-full"
                data-oid="vemg8xl"
              >
                <RefreshCw className="w-4 h-4 mr-2" data-oid="2pdhcqh" />
                Try Again
              </Button>

              <Button
                onClick={this.handleReload}
                variant="outline"
                className="w-full"
                data-oid="znykxuq"
              >
                Reload Page
              </Button>
            </div>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <details
                className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-left"
                data-oid="z8.kf7l"
              >
                <summary
                  className="cursor-pointer text-sm font-medium text-red-700 dark:text-red-300"
                  data-oid=":-6qm41"
                >
                  Error Details (Development Only)
                </summary>
                <pre
                  className="mt-2 text-xs text-red-600 dark:text-red-400 whitespace-pre-wrap"
                  data-oid="1jcb6pl"
                >
                  {this.state.error.name}: {this.state.error.message}
                  {this.state.error.stack && "\n\n" + this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
